import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Car, Bus, Building2, Users, ArrowRight, ArrowLeft, CheckCircle, 
  Phone, Mail, User, Building, Sparkles, UserCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function PartnerSignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [partnerData, setPartnerData] = useState(null);
  const [salesRep, setSalesRep] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    partner_type: '',
    company_name: '',
    recruited_by_rep_code: ''
  });

  // Check for sales rep attribution code in URL
  useEffect(() => {
    const repCode = searchParams.get('rep');
    if (repCode) {
      setFormData(prev => ({ ...prev, recruited_by_rep_code: repCode.toUpperCase() }));
      // Validate the rep code
      api.get(`/api/sales-reps/by-code/${encodeURIComponent(repCode)}`)
        .then(res => {
          if (res.data.valid) {
            setSalesRep(res.data.rep);
          }
        })
        .catch(() => {});
    }
  }, [searchParams]);

  const partnerTypes = [
    { value: 'taxi', icon: Car, label: 'Taxi Driver', description: 'I drive a taxi and pick up tourists' },
    { value: 'tour_bus', icon: Bus, label: 'Tour Bus Operator', description: 'I operate tour buses or transport groups' },
    { value: 'hotel', icon: Building2, label: 'Hotel / Resort Staff', description: 'I work at a hotel or resort (concierge, front desk)' },
    { value: 'other', icon: Users, label: 'Other', description: 'Tour guide, travel agent, or other tourism role' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.partner_type) {
      toast.error('Please select what type of partner you are');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/api/partners/signup', formData);
      setPartnerData(res.data.partner);
      setSuccess(true);
      toast.success('Welcome to the Partner Program!');
    } catch (err) {
      // Handle both string and array error formats from API
      let errorMsg = 'Failed to sign up';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0].msg || 'Validation error';
      }
      toast.error(errorMsg);
    }
    setLoading(false);
  };

  if (success && partnerData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto"
          >
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-3xl p-8 border border-green-200 dark:border-green-800 text-center">
              <div className="w-20 h-20 rounded-full bg-green-500 text-white flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10" />
              </div>
              
              <h1 className="text-3xl font-bold text-foreground mb-2">Welcome, {partnerData.name}!</h1>
              <p className="text-muted-foreground mb-6">You're now a Bulaccino Cafe Partner</p>
              
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 shadow-sm">
                <p className="text-sm text-muted-foreground mb-2">Your Unique Partner Code</p>
                <p className="text-4xl font-mono font-bold text-primary tracking-wider" data-testid="partner-code">
                  {partnerData.code}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Share this code with tourists for them to get 5% off
                </p>
              </div>
              
              <div className="space-y-3 text-left mb-8">
                <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl">
                  <Sparkles className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">Check your phone!</p>
                    <p className="text-sm text-muted-foreground">We sent your code via SMS too</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <Link to="/partner" className="block">
                  <Button className="w-full" size="lg" data-testid="go-to-dashboard-btn">
                    Go to Partner Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full" size="lg">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="partner-signup-page">
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link to="/partner-program" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Partner Program
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                Join the Bulaccino Cafe Partner Program
              </h1>
              <p className="text-lg text-muted-foreground">
                Sign up in 30 seconds and start earning rewards
              </p>
              
              {/* Sales Rep Attribution Banner */}
              {salesRep && (
                <div className="mt-4 inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                  <UserCheck className="w-4 h-4" />
                  Referred by {salesRep.name}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-sm">
              {/* Partner Type Selection */}
              <div className="mb-8">
                <Label className="text-base font-medium mb-4 block">What type of partner are you?</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {partnerTypes.map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, partner_type: type.value })}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        formData.partner_type === type.value
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`partner-type-${type.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          formData.partner_type === type.value
                            ? 'bg-primary text-white'
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          <type.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{type.label}</p>
                          <p className="text-xs text-muted-foreground">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Personal Info */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="partner-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="e.g., 999 1234"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1"
                    required
                    data-testid="partner-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">This is how you'll log in and redeem rewards</p>
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email (Optional)
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                    data-testid="partner-email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Company/Hotel Name (Optional)
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    placeholder="e.g., Fiji Tours Ltd, Hilton Fiji"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="mt-1"
                    data-testid="partner-company-input"
                  />
                </div>
              </div>

              {/* Benefits Reminder */}
              <div className="mt-6 p-4 bg-muted/50 rounded-xl">
                <p className="text-sm font-medium text-foreground mb-2">What you'll get:</p>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>✓ Your unique BULA code to share with tourists</p>
                  <p>✓ 1 free coffee for every 100 FJD in referrals</p>
                  <p>✓ VIP status (10% off for life) at 1,000 FJD</p>
                  <p>✓ Real-time tracking dashboard</p>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full mt-6"
                size="lg"
                disabled={loading}
                data-testid="partner-signup-submit"
              >
                {loading ? 'Creating Your Account...' : 'Join Partner Program'}
                {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
              </Button>

              <p className="text-xs text-center text-muted-foreground mt-4">
                By signing up, you agree to share your code responsibly with tourists visiting Fiji.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
